/**
 * QuickCruit Resume Reality Checker - Main Interface
 * 
 * The honest "Will You Actually Hear Back?" calculator.
 * No fake AI, no participation trophies, just statistical reality.
 * 
 * @author QuickCruit Engineering Team
 */

import ATSSimulator from './core/ATSSimulator.js';
import HumanScanner from './core/HumanScanner.js';
import RealityScorer from './core/RealityScorer.js';

class ResumeRealityChecker {
    constructor() {
        this.atsSimulator = new ATSSimulator();
        this.humanScanner = new HumanScanner();
        this.realityScorer = new RealityScorer();
        this.isReady = true; // No complex initialization needed
        
        console.log('🎯 Resume Reality Checker initialized - Ready to provide honest assessment');
        console.log('📊 Two-phase analysis: ATS Survival (60%) + Human Scan (40%)');
    }

    /**
     * Main analysis method - tells you if you'll actually hear back
     * 
     * @param {Object} params - Analysis parameters
     * @param {string} params.resumeText - Resume content to analyze
     * @param {string} params.jobDescription - Job posting to match against
     * @param {Object} params.options - Additional options (optional)
     * @returns {Promise<Object>} Honest reality check results
     */
    async checkHearingBackLikelihood({ resumeText, jobDescription, options = {} }) {
        const startTime = performance.now();
        
        try {
            console.log('🔍 Starting honest reality check...');
            console.log(`📄 Resume length: ${resumeText.length} characters`);
            console.log(`📋 Job description length: ${jobDescription.length} characters`);
            
            // Validate inputs first (be honest about garbage in, garbage out)
            const validation = this.validateInputs(resumeText, jobDescription);
            if (!validation.valid) {
                console.error('❌ Invalid inputs provided:', validation.errors);
                return this.getValidationErrorResults(validation.errors);
            }
            
            // Phase 1: ATS Survival Simulation (60% of final score)
            console.log('🤖 Phase 1: Simulating ATS filtering...');
            const atsResults = await this.atsSimulator.simulateATSFiltering(resumeText, jobDescription);
            console.log(`🤖 ATS Phase Complete: ${atsResults.atsScore}% survival rate`);
            
            // Phase 2: Human 6-Second Scan (40% of final score)
            console.log('👁️ Phase 2: Simulating human recruiter scan...');
            const humanResults = await this.humanScanner.simulateHumanScan(resumeText, jobDescription, atsResults);
            console.log(`👁️ Human Phase Complete: ${humanResults.humanScore}% appeal rate`);
            
            // Final Reality Calculation
            console.log('🎯 Calculating honest reality score...');
            const finalResults = await this.realityScorer.calculateRealityScore(
                atsResults, 
                humanResults, 
                resumeText, 
                jobDescription
            );
            
            const totalTime = Math.round(performance.now() - startTime);
            console.log(`✅ Analysis complete in ${totalTime}ms - Final Score: ${finalResults.score}%`);
            console.log(`📊 Likelihood: ${finalResults.likelihood.description}`);
            
            // Add metadata for tracking and debugging
            finalResults.metadata = {
                analysisDate: new Date().toISOString(),
                totalAnalysisTime: totalTime,
                resumeLength: resumeText.length,
                jobDescriptionLength: jobDescription.length,
                version: '2.0.0-honest-assessment'
            };
            
            return finalResults;
            
        } catch (error) {
            console.error('❌ Reality check failed:', error);
            return this.getSystemErrorResults(error);
        }
    }

    /**
     * Quick feedback for real-time analysis (simplified version)
     * 
     * @param {string} resumeText - Resume content
     * @param {string} jobDescription - Job posting
     * @returns {Promise<Object>} Quick reality check
     */
    async getQuickFeedback(resumeText, jobDescription) {
        try {
            console.log('⚡ Quick reality check requested...');
            
            // Just run ATS phase for speed
            const atsResults = await this.atsSimulator.simulateATSFiltering(resumeText, jobDescription);
            
            // Estimate human score based on ATS results (simplified)
            const estimatedHumanScore = Math.max(atsResults.atsScore - 10, 20);
            
            // Quick final score calculation
            const quickScore = Math.round((atsResults.atsScore * 0.6) + (estimatedHumanScore * 0.4));
            
            return {
                score: Math.min(quickScore, 95),
                likelihood: this.realityScorer.determineLikelihood(quickScore).message,
                quickSuggestions: this.generateQuickSuggestions(atsResults),
                isQuickAnalysis: true
            };
            
        } catch (error) {
            console.error('❌ Quick feedback failed:', error);
            return {
                score: 0,
                likelihood: 'Analysis failed',
                quickSuggestions: ['Please try again with valid inputs'],
                isQuickAnalysis: true
            };
        }
    }

    /**
     * Get system status and readiness
     * 
     * @returns {Object} System status
     */
    getStatus() {
        return {
            ready: this.isReady,
            version: '2.0.0-honest-assessment',
            approach: 'Two-phase ATS + Human simulation',
            philosophy: 'Honest assessment over feel-good scores',
            components: {
                atsSimulator: this.atsSimulator ? 'Ready' : 'Not initialized',
                humanScanner: this.humanScanner ? 'Ready' : 'Not initialized', 
                realityScorer: this.realityScorer ? 'Ready' : 'Not initialized'
            },
            capabilities: [
                'ATS keyword matching with synonyms',
                'Experience qualification checking',
                'Human 6-second scan simulation',
                'Quantified achievement detection',
                'Red flag pattern recognition',
                'Honest but actionable feedback'
            ]
        };
    }

    /**
     * Validate inputs before analysis
     * 
     * @private
     * @param {string} resumeText - Resume content
     * @param {string} jobDescription - Job posting
     * @returns {Object} Validation result
     */
    validateInputs(resumeText, jobDescription) {
        const errors = [];
        
        // Resume validation
        if (!resumeText || typeof resumeText !== 'string') {
            errors.push('Resume text is required and must be a string');
        } else if (resumeText.trim().length < 50) {
            errors.push('Resume is too short - need at least 50 characters');
        } else if (resumeText.length > 50000) {
            errors.push('Resume is too long - maximum 50,000 characters');
        }
        
        // Job description validation  
        if (!jobDescription || typeof jobDescription !== 'string') {
            errors.push('Job description is required and must be a string');
        } else if (jobDescription.trim().length < 20) {
            errors.push('Job description is too short - need at least 20 characters');
        } else if (jobDescription.length > 20000) {
            errors.push('Job description is too long - maximum 20,000 characters');
        }
        
        return {
            valid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Generate quick suggestions based on ATS results
     * 
     * @private
     * @param {Object} atsResults - ATS analysis results
     * @returns {Array} Quick suggestion list
     */
    generateQuickSuggestions(atsResults) {
        const suggestions = [];
        
        if (atsResults.atsScore < 60) {
            suggestions.push('Add missing technical skills mentioned in job posting');
            
            if (atsResults.details?.keywordIssues?.length > 0) {
                suggestions.push(`Focus on: ${atsResults.details.keywordIssues.slice(0, 2).join(', ')}`);
            }
        }
        
        if (atsResults.breakdown?.qualificationAlignment < 70) {
            suggestions.push('Address experience or education gaps');
        }
        
        if (suggestions.length === 0) {
            suggestions.push('Good ATS compatibility - focus on human appeal');
        }
        
        return suggestions.slice(0, 3); // Limit to 3 suggestions
    }

    /**
     * Get validation error results
     * 
     * @private
     * @param {Array} errors - Validation errors
     * @returns {Object} Error results
     */
    getValidationErrorResults(errors) {
        return {
            score: 0,
            likelihood: {
                level: 'Error',
                message: 'Invalid input provided',
                emoji: '❌',
                reality: 'Cannot analyze invalid data',
                description: '❌ Error - Cannot analyze invalid data'
            },
            feedback: {
                summary: 'Cannot analyze resume due to input validation errors.',
                criticalIssues: errors,
                strengths: []
            },
            recommendations: {
                critical: errors.map(error => ({
                    issue: 'Input validation',
                    fix: error,
                    impact: 'Required for analysis'
                })),
                important: [],
                nice_to_have: []
            },
            confidence: 'Low',
            analysisTime: 0,
            isError: true
        };
    }

    /**
     * Get system error results
     * 
     * @private
     * @param {Error} error - System error
     * @returns {Object} Error results
     */
    getSystemErrorResults(error) {
        return {
            score: 0,
            likelihood: {
                level: 'System Error',
                message: 'Analysis system failure',
                emoji: '🚨',
                reality: 'Technical issue occurred',
                description: '🚨 System Error - Technical issue occurred'
            },
            feedback: {
                summary: 'System error occurred during analysis. Please try again.',
                criticalIssues: [`System error: ${error.message}`],
                strengths: []
            },
            recommendations: {
                critical: [{
                    issue: 'System failure',
                    fix: 'Try again in a few moments',
                    impact: 'Prevents analysis'
                }],
                important: [],
                nice_to_have: []
            },
            confidence: 'Low',
            analysisTime: 0,
            isError: true,
            errorDetails: {
                message: error.message,
                stack: error.stack
            }
        };
    }

    /**
     * Dispose of resources (cleanup method)
     */
    dispose() {
        console.log('🧹 Cleaning up Resume Reality Checker...');
        this.atsSimulator = null;
        this.humanScanner = null;
        this.realityScorer = null;
        this.isReady = false;
        console.log('✅ Cleanup complete');
    }
}

// Keep backward compatibility with old naming
class ResumeScoring extends ResumeRealityChecker {
    constructor() {
        super();
        console.log('⚠️ ResumeScoring is deprecated. Use ResumeRealityChecker for new implementations.');
    }
    
    // Map old method to new method
    async analyzeResume(params) {
        return this.checkHearingBackLikelihood(params);
    }
    
    // Simplified initialize method (no-op since we don't need complex initialization)
    async initialize() {
        console.log('📝 Note: No initialization required for new honest assessment system');
        return true;
    }
}

// Export both for compatibility
export { ResumeRealityChecker, ResumeScoring };
export default ResumeRealityChecker; 